// ✅ src/pages/VehicleV2/ProgressDetailsPrint.jsx  (PRINT PAGE - SAME DESIGN STYLE AS YOUR TYRE PRINT)
// Backend:
//   GET http://nissi-india.com/api/progressDetails/print
//   params: vehType, branch, vehicleCode, vehicleName, regNo, fromDate, toDate, month, year
//
// Usage:
// navigate("/vehicle/ProgressDetailsPrint", { state: { filters } })
// OR open with querystring:
// /vehicle/ProgressDetailsPrint?vehType=O&branch=8&vehicleCode=V1&vehicleName=&regNo=&fromDate=2026-02-01&toDate=2026-02-10&month=February&year=2026

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/progressDetails";

/* helpers */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDate = (isoOrDmy) => {
  if (!isoOrDmy) return "";
  const s = String(isoOrDmy);
  if (s.includes("/")) return s; // dd/MM/yyyy already
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

const num2 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
};

function normalizeRow(r) {
  return {
    branch: pick(r, ["fld_branchname", "BranchName", "branch"]),
    progressDate: fmtDate(pick(r, ["fld_progressentrydate", "progressDate", "ProgressDate"])),
    location: pick(r, ["fld_Worklocationname", "WorkLocation", "location"]),
    status: pick(r, ["fld_status", "Status", "status"]),

    startKm: pick(r, ["fld_startingkilometer", "StartingKM", "startKm"]),
    closeKm: pick(r, ["fld_closingkilometer", "ClosingKM", "closeKm"]),
    workKm: pick(r, ["fld_Workingkm", "km", "fld_workingkm", "workKm"]),
    cumKm: pick(r, ["cumKM", "CumulativeKM", "cumKm"]),

    startHr: pick(r, ["fld_statrtinghrs", "fld_startinghrs", "StartingHR", "startHr"]),
    closeHr: pick(r, ["fld_closinghrs", "ClosingHR", "closeHr"]),
    workHr: pick(r, ["fld_Workinghr", "hrs", "fld_workinghr", "workHr"]),
    cumHr: pick(r, ["cumHR", "CumulativeHR", "cumHr"]),

    prodQty: pick(r, ["fld_productionquantity", "ProductionQty", "prodQty"]),
    dieselQty: pick(r, ["fld_dieselquantity", "DieselQty", "dieselQty"]),
    dieselCost: pick(r, ["fld_fuelrate1", "DieselCost", "dieselCost"]),
    dieselConsKm: pick(r, ["fld_diselconsumptionkm", "DieselConsKm", "dieselConsKm"]),
    dieselConsHr: pick(r, ["fld_diselconsumptionhr", "DieselConsHr", "dieselConsHr"]),
    dieselCostKm: pick(r, ["fld_diselcostkm", "DieselCostKm", "dieselCostKm"]),
    dieselCostHr: pick(r, ["fld_diselcosthr", "DieselCostHr", "dieselCostHr"]),

    isHighlight: Boolean(r?.isHighlight) || String(r?.isHighlight) === "1",
  };
}

export default function ProgressDetailsPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ Filters from state OR querystring
  const filters = useMemo(() => {
    const f = location?.state?.filters || {};

    return {
      vehType: toStr(f.vehType || searchParams.get("vehType") || "O"),
      branch: toStr(f.branch || searchParams.get("branch") || "Select"),

      vehicleCode: toStr(f.vehicleCode || searchParams.get("vehicleCode") || ""),
      vehicleName: toStr(f.vehicleName || searchParams.get("vehicleName") || ""),
      regNo: toStr(f.regNo || searchParams.get("regNo") || ""),

      fromDate: toStr(f.fromDate || searchParams.get("fromDate") || ""),
      toDate: toStr(f.toDate || searchParams.get("toDate") || ""),

      month: toStr(f.month || searchParams.get("month") || "Select"),
      year: toStr(f.year || searchParams.get("year") || "Select"),
    };
  }, [location?.state, searchParams]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const params = {
          vehType: filters.vehType,
          branch: isSelect(filters.branch) ? "Select" : filters.branch,
          vehicleCode: filters.vehicleCode,
          vehicleName: filters.vehicleName,
          regNo: filters.regNo,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          month: isSelect(filters.month) ? "Select" : filters.month,
          year: isSelect(filters.year) ? "Select" : filters.year,
        };

        const res = await axios.get(`${API_BASE}/print`, { params });
        if (!res?.data?.success) throw new Error(res?.data?.message || "Print load failed");

        const list = Array.isArray(res.data.rows) ? res.data.rows : [];
        if (alive) setRows(list);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [filters]);

  const norm = useMemo(() => rows.map(normalizeRow), [rows]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
          .hl { background-color: #fecaca !important; } /* tomato-like */
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading / Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* Header (same as your Tyre print design) */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Progress Details Report</h2>
        </div>

        {/* Filter strip (prints) */}
        <div className="border-2 border-black p-2 text-xs mb-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1">
            <div>
              <b>Type:</b> {filters.vehType || "-"}
            </div>
            <div>
              <b>Branch:</b> {isSelect(filters.branch) ? "-" : filters.branch}
            </div>
            <div>
              <b>Month:</b> {isSelect(filters.month) ? "-" : filters.month}
            </div>
            <div>
              <b>Year:</b> {isSelect(filters.year) ? "-" : filters.year}
            </div>

            <div className="md:col-span-2">
              <b>Vehicle Code:</b> {filters.vehicleCode || "-"}
            </div>
            <div className="md:col-span-2">
              <b>Vehicle Name:</b> {filters.vehicleName || "-"}
            </div>

            <div className="md:col-span-2">
              <b>Reg No:</b> {filters.regNo || "-"}
            </div>
            <div>
              <b>From:</b> {fmtDate(filters.fromDate) || "-"}
            </div>
            <div>
              <b>To:</b> {fmtDate(filters.toDate) || "-"}
            </div>
          </div>
        </div>

        {/* ✅ Table (wide like ASPX grid) */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Branch</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Progress Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Location</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Status</th>

                <th className="border border-black px-1 py-1 font-bold text-center">Starting KM</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Closing KM</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Total Working KM</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Cumulative KM</th>

                <th className="border border-black px-1 py-1 font-bold text-center">Starting HR</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Closing HR</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Total Working HR</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Cumulative HR</th>

                <th className="border border-black px-1 py-1 font-bold text-center">Production Qty</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Qty</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Cost</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Cons KM</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Cons HR</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Cost KM</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Cost HR</th>
              </tr>
            </thead>

            <tbody>
              {(!loading && norm.length === 0) ? (
                <tr>
                  <td colSpan={20} className="border border-black px-2 py-2 text-center">
                    No data to print.
                  </td>
                </tr>
              ) : (
                norm.map((r, idx) => (
                  <tr key={idx} className={r.isHighlight ? "hl" : "bg-white"}>
                    <td className="border border-black px-1 py-1 text-center">{idx + 1}</td>
                    <td className="border border-black px-1 py-1">{r.branch}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.progressDate}</td>
                    <td className="border border-black px-1 py-1">{r.location}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.status}</td>

                    <td className="border border-black px-1 py-1 text-center">{r.startKm}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.closeKm}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.workKm}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.cumKm}</td>

                    <td className="border border-black px-1 py-1 text-center">{r.startHr}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.closeHr}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.workHr}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.cumHr}</td>

                    <td className="border border-black px-1 py-1 text-center">{r.prodQty}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.dieselQty}</td>
                    <td className="border border-black px-1 py-1 text-center">{num2(r.dieselCost) || r.dieselCost}</td>
                    <td className="border border-black px-1 py-1 text-center">{num2(r.dieselConsKm) || r.dieselConsKm}</td>
                    <td className="border border-black px-1 py-1 text-center">{num2(r.dieselConsHr) || r.dieselConsHr}</td>
                    <td className="border border-black px-1 py-1 text-center">{num2(r.dieselCostKm) || r.dieselCostKm}</td>
                    <td className="border border-black px-1 py-1 text-center">{num2(r.dieselCostHr) || r.dieselCostHr}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>
      </div>
    </div>
  );
}
